import { useEffect, useState } from "react";
import { Share2, Copy, Trash2, Plus, Loader2, Lock, Eye, Clock, Link2, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { fmtUSDate } from "../lib/date";
import { toast } from "sonner";

const EXPIRY_OPTIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "Never", days: null },
];

/**
 * Modal that lets investors create / copy / revoke shareable public links to
 * their Portfolio Performance dashboard.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ShareInvestorDialog open={open} onClose={() => setOpen(false)} />
 *   <button onClick={() => setOpen(true)}>Share</button>
 */
export default function ShareInvestorDialog({ open, onClose }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // create form state
  const [form, setForm] = useState({
    title: "",
    note: "",
    expires_in_days: 30,
    password: "",
    hide_addresses: false,
    hide_property_names: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/investor-reports");
      setLinks(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  if (!open) return null;

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/investor-reports", {
        title: form.title.trim() || null,
        note: form.note.trim() || null,
        expires_in_days: form.expires_in_days,
        password: form.password.trim() || null,
        hide_addresses: form.hide_addresses,
        hide_property_names: form.hide_property_names,
      });
      toast.success("Investor link created");
      setLinks([data, ...links]);
      setForm({ title: "", note: "", expires_in_days: 30, password: "", hide_addresses: false, hide_property_names: false });
      // auto-copy the fresh URL
      copyLink(data.hash);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (hash) => {
    const url = `${window.location.origin}/investor/${hash}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied to clipboard"),
      () => toast.error("Could not copy — please copy manually")
    );
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this link? Recipients will see a 'Link revoked' message immediately.")) return;
    try {
      await api.delete(`/investor-reports/${id}`);
      setLinks(links.map((l) => (l.id === id ? { ...l, revoked: true } : l)));
      toast.success("Link revoked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to revoke");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
      onClick={onClose}
      data-testid="share-investor-dialog"
    >
      <div
        className="bg-white rounded-sm border border-slate-200 shadow-xl max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-slate-700" />
            <h2 className="text-lg font-bold text-slate-900">Share with investors</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="share-investor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50">
          <div className="text-xs text-slate-500 mb-4">
            Generate a time-limited, read-only public URL of your Portfolio Performance. Recipients do not need an account. You can revoke at any time.
          </div>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label className="overline text-slate-500">Title (optional)</label>
              <input
                type="text"
                className="input-clean w-full"
                placeholder="Q1 2026 Portfolio Update"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={200}
                data-testid="share-investor-title-input"
              />
            </div>
            <div>
              <label className="overline text-slate-500">Message to recipient (optional)</label>
              <textarea
                className="input-clean w-full"
                rows={2}
                placeholder="Sharing the latest portfolio snapshot ahead of our Tuesday call."
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                maxLength={2000}
                data-testid="share-investor-note-input"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="overline text-slate-500">Expires in</label>
                <select
                  className="input-clean w-full"
                  value={form.expires_in_days == null ? "" : String(form.expires_in_days)}
                  onChange={(e) => setForm({ ...form, expires_in_days: e.target.value === "" ? null : Number(e.target.value) })}
                  data-testid="share-investor-expiry-select"
                >
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.label} value={o.days == null ? "" : String(o.days)}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Password (optional)</label>
                <input
                  type="text"
                  className="input-clean w-full font-mono"
                  placeholder="Leave empty for no password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  maxLength={100}
                  data-testid="share-investor-password-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.hide_property_names}
                  onChange={(e) => setForm({ ...form, hide_property_names: e.target.checked })}
                  data-testid="share-investor-anon-names"
                />
                Anonymize property names (Property A, B, C…)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.hide_addresses}
                  onChange={(e) => setForm({ ...form, hide_addresses: e.target.checked })}
                  data-testid="share-investor-anon-addr"
                />
                Hide street addresses
              </label>
            </div>
            <button
              type="submit"
              disabled={creating}
              className="btn-solid-dark w-full flex items-center justify-center gap-2"
              data-testid="share-investor-create-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Generate link &amp; copy
            </button>
          </form>
        </div>

        {/* EXISTING LINKS */}
        <div className="px-6 py-5">
          <div className="overline text-slate-500 mb-3 flex items-center gap-2">
            <Link2 className="w-3.5 h-3.5" /> Your active links
          </div>
          {loading ? (
            <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : links.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-6" data-testid="share-investor-empty">
              No investor links yet. Generate your first one above.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {links.map((l) => {
                const expDate = l.expires_at ? fmtUSDate(l.expires_at) : "Never";
                const expired = l.expires_at && new Date(l.expires_at) < new Date();
                return (
                  <li key={l.id} className="py-3 flex items-center gap-3" data-testid={`share-investor-row-${l.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900 text-sm truncate">{l.title || "Portfolio Report"}</div>
                        {l.revoked && <span className="text-[10px] font-mono uppercase bg-red-100 text-red-700 px-2 py-0.5 rounded-sm">Revoked</span>}
                        {!l.revoked && expired && <span className="text-[10px] font-mono uppercase bg-slate-200 text-slate-700 px-2 py-0.5 rounded-sm">Expired</span>}
                        {l.password_protected && <span className="text-[10px] font-mono uppercase bg-violet-100 text-violet-700 px-2 py-0.5 rounded-sm flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />Password</span>}
                        {l.hide_property_names && <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-2 py-0.5 rounded-sm">Anon</span>}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono mt-0.5 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Expires {expDate}</span>
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{l.view_count} view{l.view_count === 1 ? "" : "s"}</span>
                      </div>
                    </div>
                    {!l.revoked && (
                      <button
                        onClick={() => copyLink(l.hash)}
                        className="text-slate-500 hover:text-slate-900 p-1.5 rounded-sm hover:bg-slate-100"
                        title="Copy link"
                        data-testid={`share-investor-copy-${l.id}`}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                    {!l.revoked && (
                      <button
                        onClick={() => handleRevoke(l.id)}
                        className="text-red-500 hover:text-red-700 p-1.5 rounded-sm hover:bg-red-50"
                        title="Revoke"
                        data-testid={`share-investor-revoke-${l.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
